import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';

import { cx } from './internal/cx.js';

/** Semantic chip tones aligned with Alert. */
export type ChipTone = 'neutral' | 'info' | 'warning' | 'danger' | 'success';

const TONE_CLASS: Record<ChipTone, string> = {
  neutral: 'nrx-chip--neutral',
  info: 'nrx-chip--info',
  warning: 'nrx-chip--warning',
  danger: 'nrx-chip--danger',
  success: 'nrx-chip--success',
};

export interface ChipProps extends ComponentPropsWithoutRef<'span'> {
  children: ReactNode;
  /** Optional leading icon slot. */
  icon?: ReactNode;
  /** When provided, renders a remove button. */
  onDelete?: () => void;
  /** Accessible label for the delete button. */
  deleteLabel?: string;
  /** Visual fill style. */
  variant?: 'filled' | 'outlined';
  /** Semantic color tone (Alert-aligned). Defaults to neutral. */
  tone?: ChipTone;
}

/** Compact tag for filters, selections, and status chips. */
export function Chip({
  children,
  icon,
  onDelete,
  deleteLabel = 'Remove',
  variant = 'filled',
  tone = 'neutral',
  className,
  ...props
}: ChipProps): JSX.Element {
  return (
    <span
      className={cx(
        'nrx-chip',
        TONE_CLASS[tone],
        variant === 'outlined' && 'nrx-chip--outlined',
        className,
      )}
      {...props}
    >
      {icon ? <span className="nrx-chip__icon">{icon}</span> : null}
      <span className="nrx-chip__label">{children}</span>
      {onDelete ? (
        <button
          type="button"
          className="nrx-chip__delete"
          aria-label={deleteLabel}
          onClick={onDelete}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}
